import {
  ComposedChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { SlidersHorizontal } from "lucide-react";
import { SectionPanel } from "@/components/ui/section-panel";
import {
  splitForecast,
  nowKeyOf,
  type OperationalLoopPoint,
} from "./operational-loop-data";
import {
  CHART_MARGIN,
  hoverHandlers,
  loopXAxisProps,
  loopYAxis,
  LOOP_STAGE_ACCENT,
  type HoverHandler,
} from "./chart-shared";
import { nowMarker } from "./NowMarker";
import { applyMarkers } from "./ApplyMarkers";
import { ForecastTooltip } from "./ForecastTooltip";
import type { ApplyMarker, RecipeAxisLabels } from "./operational-loop-adapter";

export type RecipeVisibility = { percent: boolean; temp: boolean };

// 3. Ohjaus — what this recipe wrote to the equipment's ports. Perusura and mitattu live
// on Yhteisvaikutus / Verifiointi (ohjaus toteutui?), not here — this card is the commanded
// output only. History is solid, forecast dashed. Apply markers show where each recipe
// landed. `visible` narrows the chart to the axes the recipes in view actually wrote to;
// `null` shows both.
//
// Axis titles (`labels`) come from the backend's KB-sourced series labels
// (operational-loop-adapter.ts's `recipeAxisLabels`) — never a per-equipment-type lookup table
// here. Both axes are offset-native (0 = hold): `%` for ventilation level, `°C` for
// heating-curve and AHU supply-air temperature. An equipment type with only a temperature
// port simply leaves the percent axis empty — a real gap, never a fabricated line —
// rather than this component guessing a label for data that isn't there.
export function RecipeChart({
  points,
  equipmentName,
  labels,
  markers,
  visible,
  unavailable,
  onHover,
}: {
  points: OperationalLoopPoint[];
  equipmentName: string;
  labels: RecipeAxisLabels;
  markers: ApplyMarker[];
  visible: RecipeVisibility | null;
  unavailable?: string;
  onHover?: HoverHandler;
}) {
  const show = visible ?? { percent: true, temp: true };
  const rows = splitForecast(points, [
    "fanPowerOptimizedPct",
    "supplyTempOptimizedC",
  ]);
  const nowKey = nowKeyOf(points);
  const firstKey = rows[0]?.key ?? nowKey;
  const markerAxis = show.percent ? "power" : "temp";
  const subtitle = [
    show.percent && `${labels.percentLabel} (%)`,
    show.temp && `${labels.tempLabel.toLowerCase()} (°C)`,
  ]
    .filter(Boolean)
    .join(" ja ");

  return (
    <SectionPanel
      icon={SlidersHorizontal}
      title={`Optimointiresepti · ${equipmentName}`}
      subtitle={`${subtitle} — komento: mitä resepti kirjoitti laitteelle`}
      accentColor={LOOP_STAGE_ACCENT.recipe}
    >
      {unavailable ? (
        <p className="text-xs text-muted-foreground">{unavailable}</p>
      ) : (
        <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={CHART_MARGIN}
            syncId="operational-loop"
            {...hoverHandlers(onHover)}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="var(--border)"
              strokeOpacity={0.5}
            />
            <XAxis {...loopXAxisProps(rows)} />
            <YAxis
              {...loopYAxis("left", "power", {
                labeled: show.percent,
                // Offset-native ventilation (and any other % recipe): 0 = hold, not "fan off".
                // A fixed 0–100 domain hid negative setbacks and treated 0 as off.
                domain: ["auto", "auto"],
              })}
            />
            <YAxis
              {...loopYAxis("right", "temp", {
                labeled: show.temp,
                // Offset-native °C (heating-curve and AHU supply-air): 0 = hold.
                // Auto keeps round ticks for whichever port the equipment has.
                domain: ["auto", "auto"],
              })}
            />
            <Tooltip content={<ForecastTooltip />} />

            {nowMarker({ x: nowKey, firstX: firstKey, yAxisId: markerAxis })}
            {applyMarkers(markers, markerAxis)}
            {show.percent && (
              <ReferenceLine
                y={0}
                yAxisId="power"
                stroke="var(--border)"
                strokeDasharray="3 3"
                strokeOpacity={0.85}
              />
            )}
            {show.temp && (
              <ReferenceLine
                y={0}
                yAxisId="temp"
                stroke="var(--border)"
                strokeDasharray="3 3"
                strokeOpacity={0.85}
              />
            )}

            {show.percent && (
              <Line
                yAxisId="power"
                type="stepAfter"
                name={`Optimoitu: ${labels.percentLabel.toLowerCase()} (Historia)`}
                dataKey="fanPowerOptimizedPctPast"
                stroke="#3b82f6"
                strokeWidth={2}
                strokeOpacity={0.55}
                dot={false}
              />
            )}
            {show.percent && (
              <Line
                yAxisId="power"
                type="stepAfter"
                name={`Optimoitu: ${labels.percentLabel.toLowerCase()} (Ennuste)`}
                dataKey="fanPowerOptimizedPctFuture"
                stroke="#3b82f6"
                strokeWidth={2.5}
                strokeDasharray="5 5"
                dot={false}
              />
            )}
            {show.temp && (
              <Line
                yAxisId="temp"
                type="stepAfter"
                name={`Optimoitu: ${labels.tempLabel.toLowerCase()} (Historia)`}
                dataKey="supplyTempOptimizedCPast"
                stroke="#ef4444"
                strokeWidth={2}
                strokeOpacity={0.55}
                dot={false}
              />
            )}
            {show.temp && (
              <Line
                yAxisId="temp"
                type="stepAfter"
                name={`Optimoitu: ${labels.tempLabel.toLowerCase()} (Ennuste)`}
                dataKey="supplyTempOptimizedCFuture"
                stroke="#ef4444"
                strokeWidth={2.5}
                strokeDasharray="5 5"
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
        </div>
      )}
    </SectionPanel>
  );
}
